import { useDataStore } from '@/lib/machine/use-data-store'
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction
} from '@ui/components/ui/alert-dialog'
import { Button } from '@ui/components/ui/button'
import { Label } from '@ui/components/ui/label'
import { Switch } from '@ui/components/ui/switch'
import { MachineSystemData, getDefaultAlarmOverrides } from '@uv/machine'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { alarmKeyFromIndex, formatAlarmName } from '../../utils/format-alarm-name'

type Props = {
  showDialog: boolean
  closeDialog: () => void
  closeSettings: () => void
}

export const SystemOverrideDialog = ({ showDialog, closeDialog, closeSettings }: Props) => {
  const { t } = useTranslation()
  const sendMachineAction = useDataStore(s => s.sendMachineAction)
  const machineConfiguration = useDataStore(s => s.machineConfiguration)
  const refreshData = useDataStore(s => s.refreshData)

  const alarmCount = refreshData?.ALARMS.length === 8 ? 8 : 9

  const getOverridesForAlarmCount = (
    overrides: MachineSystemData['OVERRIDES'],
    count: 8 | 9
  ): MachineSystemData['OVERRIDES'] => {
    if (count === 8) {
      const [uvHot, airHot, motor, airSupply, head, pressure, slip, sensor] = overrides
      return [uvHot, airHot, motor, airSupply, head, pressure, slip, sensor]
    }

    if (overrides.length === 9) return overrides

    const [uvHot, airHot, motor, airSupply, head, pressure, slip, sensor] = overrides
    return [uvHot, airHot, motor, airSupply, head, pressure, slip, sensor, false]
  }

  const getCurrentMachineOverrides = (): MachineSystemData['OVERRIDES'] => {
    return getOverridesForAlarmCount(
      machineConfiguration?.overrides || getDefaultAlarmOverrides(),
      alarmCount
    )
  }

  const [overridesInputs, setOverridesInputs] = useState<MachineSystemData['OVERRIDES']>(
    getCurrentMachineOverrides()
  )

  useEffect(() => {
    if (!showDialog) return
    setOverridesInputs(getCurrentMachineOverrides())
  }, [alarmCount, machineConfiguration?.overrides, showDialog])

  const toggleFirmware1Override = (
    overrides: [boolean, boolean, boolean, boolean, boolean, boolean, boolean, boolean],
    index: number
  ): MachineSystemData['OVERRIDES'] => {
    const [uvHot, airHot, motor, airSupply, head, pressure, slip, sensor] = overrides
    const nextValue = !overrides[index]

    switch (index) {
      case 0:
        return [nextValue, airHot, motor, airSupply, head, pressure, slip, sensor]
      case 1:
        return [uvHot, nextValue, motor, airSupply, head, pressure, slip, sensor]
      case 2:
        return [uvHot, airHot, nextValue, airSupply, head, pressure, slip, sensor]
      case 3:
        return [uvHot, airHot, motor, nextValue, head, pressure, slip, sensor]
      case 4:
        return [uvHot, airHot, motor, airSupply, nextValue, pressure, slip, sensor]
      case 5:
        return [uvHot, airHot, motor, airSupply, head, nextValue, slip, sensor]
      case 6:
        return [uvHot, airHot, motor, airSupply, head, pressure, nextValue, sensor]
      case 7:
        return [uvHot, airHot, motor, airSupply, head, pressure, slip, nextValue]
      default:
        return overrides
    }
  }

  const toggleFirmware2Override = (
    overrides: [boolean, boolean, boolean, boolean, boolean, boolean, boolean, boolean, boolean],
    index: number
  ): MachineSystemData['OVERRIDES'] => {
    const [uvHot, airHot, motor, airSupply, head, pressure, slip, sensor, uvPower] = overrides
    const nextValue = !overrides[index]

    switch (index) {
      case 0:
        return [nextValue, airHot, motor, airSupply, head, pressure, slip, sensor, uvPower]
      case 1:
        return [uvHot, nextValue, motor, airSupply, head, pressure, slip, sensor, uvPower]
      case 2:
        return [uvHot, airHot, nextValue, airSupply, head, pressure, slip, sensor, uvPower]
      case 3:
        return [uvHot, airHot, motor, nextValue, head, pressure, slip, sensor, uvPower]
      case 4:
        return [uvHot, airHot, motor, airSupply, nextValue, pressure, slip, sensor, uvPower]
      case 5:
        return [uvHot, airHot, motor, airSupply, head, nextValue, slip, sensor, uvPower]
      case 6:
        return [uvHot, airHot, motor, airSupply, head, pressure, nextValue, sensor, uvPower]
      case 7:
        return [uvHot, airHot, motor, airSupply, head, pressure, slip, nextValue, uvPower]
      case 8:
        return [uvHot, airHot, motor, airSupply, head, pressure, slip, sensor, nextValue]
      default:
        return overrides
    }
  }

  const toggleOverride = (index: number) => {
    setOverridesInputs(prev => {
      if (prev.length === 8) return toggleFirmware1Override(prev, index)
      return toggleFirmware2Override(prev, index)
    })
  }

  return (
    <AlertDialog open={showDialog}>
      <AlertDialogContent className="min-w-[40vw] p-16">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-3xl mb-4 text-center">
            {t('settings.service-mode.title')}
          </AlertDialogTitle>

          <AlertDialogDescription className="text-lg text-center">
            {t('settings.service-mode.description')}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="grid grid-cols-2 gap-y-4 gap-x-16 mt-6 mx-8">
          {overridesInputs.map((override, index) => {
            const alarmKey = alarmKeyFromIndex(index)
            const name = formatAlarmName(alarmKey)
            const id = `override-${index}`

            return (
              <div className="flex items-center justify-between" key={id}>
                <Label htmlFor={id}>{name}</Label>
                <Switch
                  id={id}
                  checked={override}
                  onCheckedChange={() => toggleOverride(index)}
                />
              </div>
            )
          })}
        </div>

        <AlertDialogFooter className="self-end gap-4 mt-16">
          <AlertDialogCancel
            className="w-1/2 p-8 text-md font-bold rounded-xl"
            onClick={closeDialog}
          >
            {t('settings.service-mode.dismiss-button')}
          </AlertDialogCancel>
          <AlertDialogAction asChild>
            <Button
              className="w-1/2 p-8 text-md font-bold rounded-xl"
              variant="destructive"
              onClick={() => {
                sendMachineAction({ in: 'overrides_configure', overrides: overridesInputs })
                closeSettings()
                closeDialog()
              }}
            >
              {t('settings.service-mode.bypass-button')}
            </Button>
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
